import { useEffect, useState } from 'react'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { type CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetMeQuery, useUpdateUserIntegrationMutation } from '../../../../../store/user/user'
import { IntegrationServiceId } from '../../../../../common/integrations'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { merge } from '../../../utils'
import { removeEmptyItems } from '../../../../../common/util'
import { type AdvisorEngineInboundContactSyncSettingsFilters, type AdvisorEngineSettingsFormValues } from './models'

const ADVISOR_ENGINE_CONTACT_SYNC_MAX_OBJECT_COUNT = 500

export const useAdvisorEngineContactSync = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: 'advisorengine',
    direction: 'inbound',
    object: 'contact_operation_hashed',
    scheduling: '*/30 * * * *',
    payload: {
      maxObjectCount: ADVISOR_ENGINE_CONTACT_SYNC_MAX_OBJECT_COUNT,
    },
  } as CheckpointRequest
  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)
  const [createCheckpoint, { isLoading: isCreateCheckpointLoading }] = useCreateCheckpointMutation()
  const [updateCheckpoint, { isLoading: isUpdateCheckpointLoading }] = useUpdateCheckpointMutation()
  const { data: userData, isFetching: isGetMeQueryFetching } = useGetMeQuery({ accountId: accountId })
  const [updateUserIntegration, { isLoading: isUpdateUserIntegrationLoading }] = useUpdateUserIntegrationMutation()
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [serviceConfig, setServiceConfig] = useState<{ [key: string]: any } | null>(null)
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, 'advisorengine')
      setIsSyncDisabled(serviceIntegration?.isSyncDisabled ?? false)
    }
  }, [account])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    if (userData) {
      const integration = userData.integrations?.find((i) => i.serviceId === IntegrationServiceId.ADVISOR_ENGINE)
      if (integration) {
        setServiceConfig(integration.serviceConfig ?? null)
      }
    }
  }, [userData])

  const save = async (data: AdvisorEngineSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
      const integration = userData?.integrations?.find((i) => i.serviceId === IntegrationServiceId.ADVISOR_ENGINE)
      if (integration) {
        const filters: AdvisorEngineInboundContactSyncSettingsFilters = {
          active: data.inbound.contact.filters.active ?? undefined,
          contactType: data.inbound.contact.filters.contactType || undefined,
          tags: (data.inbound.contact.filters.tags ?? []).length > 0 ? data.inbound.contact.filters.tags : undefined,
        }
        const mergedServiceConfig = merge(integration.serviceConfig || {}, { inbound: { contact: { filters } } })
        await updateUserIntegration({
          accountId,
          integrationName: integration.serviceId,
          integrationId: integration.id,
          payload: { serviceConfig: removeEmptyItems(mergedServiceConfig) },
        })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  const saveAutoSync = async (active: boolean) => {
    try {
      if (autoSync == null) {
        if (active) await createCheckpoint(checkpointRequest).unwrap()
      } else {
        await updateCheckpoint({ ...checkpointRequest, active }).unwrap()
      }
    } catch (e) {
      console.error(e)
    }
  }

  return {
    autoSync,
    serviceConfig,
    isSyncDisabled,
    isAccountFetching,
    isFetching: !isUpdating && (isGetMeQueryFetching || isGetCheckpointFetching),
    isSaving: isCreateCheckpointLoading || isUpdateCheckpointLoading || isUpdateUserIntegrationLoading,
    save,
    saveAutoSync,
  }
}
